<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("claims.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("claims.error") }}</div>
    <template v-else>
      <!-- header: title + search + export -->
      <div class="row between" style="margin-bottom: 14px">
        <div>
          <div class="pagetitle">{{ $t("claims.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("claims.title") }}</div>
        </div>
        <div class="searchbar grow" style="max-width: 340px">
          🔎
          <input
            type="text"
            :placeholder="$t('claims.searchPlaceholder')"
            v-model.trim="q"
            @input="reload"
          />
        </div>
        <button class="btn ghost sm" type="button" @click="exportCsv">⬇️ {{ $t("claims.export") }}</button>
        <button class="btn primary sm" type="button" data-test="claim-add" @click="goAdd">➕ {{ $t("claims.create.open") }}</button>
      </div>

      <!-- E1: the form page redirects back with ?saved=<id>, so the list is
           where the claim is confirmed. Only a fully-successful submit lands
           here — a partial attachment failure keeps the user on the form, where
           the failing file names are listed. -->
      <div v-if="createdId" class="callout" style="margin-bottom: 14px" data-test="claim-saved-note">
        ✅ {{ $t("claims.create.success", { id: createdId }) }}
        <div v-if="uploadedCount" class="sm" style="margin-top: 4px">
          📎 {{ $t("claims.create.attach.uploaded", { n: uploadedCount }) }}
        </div>
      </div>

      <!-- per-status count cards (from the full, unfiltered set) -->
      <div class="kpis" style="grid-template-columns: repeat(5, 1fr)">
        <div v-for="c in counts" :key="c.status" class="kpi">
          <div class="ic" :style="{ background: statusBg(c.status) }">{{ statusIcon(c.status) }}</div>
          <div class="v mono">{{ fInt(c.count) }}</div>
          <div class="l">{{ statusLabel(c.status) }}</div>
        </div>
      </div>

      <!-- status filter chips -->
      <div class="panel mt" style="margin-bottom: 14px">
        <div class="toolbar">
          <span class="sm muted" style="font-weight: 700">{{ $t("claims.filtersLabel") }}</span>
          <button class="chip" :class="{ on: activeStatus === '' }" type="button" @click="setStatus('')">
            {{ $t("claims.allStatuses") }}
          </button>
          <button
            v-for="s in statuses"
            :key="s"
            class="chip"
            :class="{ on: activeStatus === s }"
            type="button"
            @click="setStatus(s)"
          >
            {{ statusLabel(s) }}
          </button>
        </div>
      </div>

      <!-- HR-follows-only note -->
      <div class="callout" style="margin-bottom: 14px">ℹ️ {{ $t("claims.hrNote") }}</div>

      <!-- claims table -->
      <div class="panel mt">
        <div class="panel__head">
          <h4>📄 {{ $t("claims.tableTitle") }}</h4>
          <span class="tag muted mono">{{ fInt(rows.length) }}</span>
        </div>
        <div class="panel__body p0">
          <div class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th>{{ $t("claims.th.no") }}</th>
                  <th>{{ $t("claims.th.employee") }}</th>
                  <th>{{ $t("claims.th.branch") }}</th>
                  <th>{{ $t("claims.th.type") }}</th>
                  <th>{{ $t("claims.th.amount") }}</th>
                  <th>{{ $t("claims.th.date") }}</th>
                  <th>{{ $t("claims.th.status") }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="c in rows">
                  <tr :key="c.id">
                    <td class="mono">#{{ c.id }}</td>
                    <td>{{ resolveName(nameMap, c.employeeId) }}</td>
                    <td><span class="tag purple">{{ resolveName(policyMap, c.policyId) }}</span></td>
                    <td>{{ c.serviceType }}</td>
                    <td class="mono">{{ fMoney(c.amount) }}</td>
                    <td>{{ fDate(c.invoiceDate) }}</td>
                    <td><span class="tag" :class="claimTag(c.status)">{{ statusLabel(c.status) }}</span></td>
                    <td>
                      <button class="btn ghost sm" type="button" @click="toggle(c.id)">
                        {{ expandedId === c.id ? $t("claims.hide") : $t("claims.view") }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="expandedId === c.id" :key="c.id + '-d'">
                    <td colspan="8" style="background: #f7fafb">
                      <div class="row" style="gap: 22px; flex-wrap: wrap">
                        <span class="sm"><span class="muted">{{ $t("claims.detail.provider") }}:</span> {{ c.provider || "—" }}</span>
                        <span class="sm"><span class="muted">{{ $t("claims.detail.invoiceNo") }}:</span> <b class="mono">{{ c.invoiceNo || "—" }}</b></span>
                        <span class="sm"><span class="muted">{{ $t("claims.detail.invoiceDate") }}:</span> {{ fDate(c.invoiceDate) }}</span>
                        <span class="sm"><span class="muted">{{ $t("claims.detail.status") }}:</span> <span class="tag" :class="claimTag(c.status)">{{ statusLabel(c.status) }}</span></span>
                        <span class="sm"><span class="muted">{{ $t("claims.detail.decidedAmount") }}:</span> <b class="mono">{{ detailOf(c).decidedAmount == null ? "—" : fMoney(detailOf(c).decidedAmount) }}</b></span>
                        <span class="sm"><span class="muted">{{ $t("claims.detail.source") }}:</span> {{ sourceLabel(c.source) }}</span>
                      </div>
                      <div class="sm mt">
                        <span class="muted">{{ $t("claims.detail.reason") }}:</span>
                        <span v-if="reasonOf(c)">{{ reasonOf(c) }}</span>
                        <span v-else class="muted">{{ $t("claims.detail.noReason") }}</span>
                      </div>
                      <div class="adj mt" data-test="adjudication">
                        <div class="adj__title">🧮 {{ $t("claims.adjudication.title") }}</div>
                        <div v-if="detailLoading === c.id" class="sm muted">{{ $t("claims.loading") }}</div>
                        <div v-else-if="!adjOf(c)" class="sm muted">{{ $t("claims.adjudication.none") }}</div>
                        <template v-else>
                          <div class="row" style="gap: 22px; flex-wrap: wrap">
                            <span class="sm"><span class="muted">{{ $t("claims.adjudication.claimAmount") }}:</span> <b class="mono">{{ money(adjOf(c).claimAmount) }}</b></span>
                            <span class="sm"><span class="muted">{{ $t("claims.adjudication.covered") }}:</span> <b class="mono">{{ money(adjOf(c).coveredAmount) }}</b></span>
                            <span class="sm"><span class="muted">{{ $t("claims.adjudication.employeeShare") }}:</span> <b class="mono">{{ money(adjOf(c).employeeShare) }}</b></span>
                            <span class="sm"><span class="muted">{{ $t("claims.adjudication.decided") }}:</span> <b class="mono">{{ money(adjOf(c).decidedAmount) }}</b></span>
                            <span v-if="adjOf(c).capRemaining != null" class="sm"><span class="muted">{{ $t("claims.adjudication.capRemaining") }}:</span> <b class="mono">{{ money(adjOf(c).capRemaining) }}</b></span>
                          </div>
                          <div v-if="adjOf(c).overriddenBelowCovered" class="note info mt">{{ $t("claims.adjudication.overridden") }}</div>
                          <table v-if="adjOf(c).rules.length" class="table mt adj__rules">
                            <thead>
                              <tr>
                                <th>{{ $t("claims.adjudication.rule") }}</th>
                                <th>{{ $t("claims.adjudication.detail") }}</th>
                                <th>{{ $t("claims.adjudication.before") }}</th>
                                <th>{{ $t("claims.adjudication.after") }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(r, i) in adjOf(c).rules" :key="i">
                                <td>{{ ruleLabel(r) }}</td>
                                <td class="mono">{{ r.detail || "—" }}</td>
                                <td class="mono">{{ money(r.before) }}</td>
                                <td class="mono">{{ money(r.after) }}</td>
                              </tr>
                            </tbody>
                          </table>
                          <div v-else class="sm muted mt">{{ $t("claims.adjudication.noRules") }}</div>
                        </template>
                      </div>
                      <div class="sm mt">
                        <span class="muted">{{ $t("claims.detail.documents") }}:</span>
                        <span v-if="detailLoading === c.id" class="muted">{{ $t("claims.loading") }}</span>
                        <template v-else-if="docsOf(c).length">
                          <button
                            v-for="d in docsOf(c)"
                            :key="d.id"
                            class="btn ghost sm"
                            type="button"
                            style="margin-inline-end: 6px"
                            :disabled="openingDoc === d.id"
                            @click="openDoc(c, d)"
                          >📎 {{ $t("claims.detail.openDoc", { n: d.index }) }}<template v-if="d.kind"> · {{ d.kind }}</template></button>
                        </template>
                        <span v-else class="muted">{{ $t("claims.detail.noDocs") }}</span>
                        <span v-if="docError" class="xs err" style="margin-inline-start: 8px">{{ docError }}</span>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="rows.length === 0">
                  <td colspan="8" class="state">{{ $t("claims.empty") }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiGetBlob } from "../../api/vivo/http";
import { companyPath } from "../../api/vivo/session";
import {
  STATUS_ORDER,
  claimTag,
  buildMap,
  resolveName,
  statusCounts,
  toCsv,
  decisionReason,
  documentList,
  documentFilePath,
  adjudicationView,
} from "./claims-vm";
// E1: the create form, its validation, its attachment upload and todayIso()
// all moved to claim-form.vue / claims-vm.js. This screen is a list again.

const STATUS_ICON = {
  registered: "🆕",
  at_insurer: "🔍",
  missing_doc: "📎",
  accepted: "✅",
  rejected: "❌",
};

const STATUS_BG = {
  registered: "var(--v-accent-soft)",
  at_insurer: "var(--v-warn-soft)",
  missing_doc: "var(--v-line-2)",
  accepted: "var(--v-ok-soft)",
  rejected: "var(--v-danger-soft)",
};

export default {
  name: "VivoClaims",
  data() {
    return {
      rows: [],
      allClaims: [],
      nameMap: {},
      policyMap: {},
      policyRows: [],
      activeStatus: "",
      q: "",
      expandedId: null,
      details: {},
      detailLoading: null,
      openingDoc: null,
      docError: "",
      // fed by the ?saved=<id> handover from /claims/new
      createdId: "",
      uploadedCount: 0,
      statuses: STATUS_ORDER,
      loading: true,
      error: false,
    };
  },
  async mounted() {
    try {
      const [emps, policies, claims] = await Promise.all([
        apiGet("/employees", { limit: 100 }),
        apiGet(companyPath("policies"), { limit: 100 }),
        apiGet("/claims"),
      ]);
      this.nameMap = buildMap((emps && emps.data) || [], "fullName");
      this.policyRows = (policies && policies.data) || [];
      this.policyMap = buildMap(this.policyRows, "number");
      this.allClaims = (claims && claims.data) || [];
      this.rows = this.allClaims;
      // Deep link from a notification: /claims?claim=<id> opens that claim's detail.
      const target = this.$route && this.$route.query && this.$route.query.claim;
      if (target && this.allClaims.some((c) => String(c.id) === String(target))) this.toggle(String(target));
      // E1: /claims/new redirects here with ?saved=<id> after a successful
      // submit. Strip the query afterwards, or a refresh would re-announce a
      // claim that was registered minutes ago.
      const saved = this.$route && this.$route.query && this.$route.query.saved;
      if (saved) {
        this.createdId = String(saved);
        this.uploadedCount = Number(this.$route.query.uploaded || 0) || 0;
        this.$router.replace({ name: "claims" });
      }
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  computed: {
    employeeOptions() {
      return Object.keys(this.nameMap).map((id) => ({ id, name: this.nameMap[id] }));
    },
    policyOptions() {
      return this.policyRows
        .filter((p) => !p.status || p.status === "active")
        .map((p) => ({ id: String(p.id), label: p.classLabel ? p.number + " · " + p.classLabel : p.number }));
    },
    counts() {
      return statusCounts(this.allClaims);
    },
  },
  methods: {
    async reload() {
      try {
        const params = {};
        if (this.activeStatus) params.status = this.activeStatus;
        if (this.q) params.q = this.q;
        const res = await apiGet("/claims", params);
        this.rows = (res && res.data) || [];
        this.expandedId = null;
      } catch (e) {
        /* keep the current rows on a transient filter error */
      }
    },
    setStatus(s) {
      this.activeStatus = s;
      this.reload();
    },
    async toggle(id) {
      this.docError = "";
      this.expandedId = this.expandedId === id ? null : id;
      if (this.expandedId === null || this.details[id]) return;
      // The list row may not carry documents / the reviewer's reason: load the detail.
      this.detailLoading = id;
      try {
        const d = await apiGet(`/claims/${id}`);
        this.$set(this.details, id, d || {});
      } catch (e) {
        this.$set(this.details, id, {});
      } finally {
        this.detailLoading = null;
      }
    },
    detailOf(c) {
      return Object.assign({}, c, this.details[c.id] || {});
    },
    adjOf(c) {
      return adjudicationView(this.detailOf(c));
    },
    ruleLabel(r) {
      if (!r.known) return r.code || "—";
      return this.$t("claims.adjudication.rules." + r.code);
    },
    money(n) {
      return n == null ? "—" : this.fMoney(n);
    },
    reasonOf(c) {
      return decisionReason(this.detailOf(c));
    },
    docsOf(c) {
      return documentList(this.detailOf(c));
    },
    async openDoc(c, d) {
      this.docError = "";
      this.openingDoc = d.id;
      try {
        const blob = await apiGetBlob(documentFilePath(c.id, d.id));
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        this.docError = this.$t("claims.detail.docError");
      } finally {
        this.openingDoc = null;
      }
    },
    // E1: registering a claim is its own page now (/claims/new); the long form
    // no longer pushes this table out of view.
    goAdd() {
      if (this.$route.name !== "claims-new") this.$router.push({ name: "claims-new" });
    },
    async refreshAll() {
      try {
        const claims = await apiGet("/claims");
        this.allClaims = (claims && claims.data) || [];
        await this.reload();
      } catch (e) {
        /* the created claim is saved; list refresh is best-effort */
      }
    },
    exportCsv() {
      const csv = toCsv(this.rows, this.nameMap, this.policyMap);
      if (typeof document === "undefined" || typeof URL === "undefined" || !URL.createObjectURL) return;
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "claims.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    resolveName(map, id) {
      return resolveName(map, id);
    },
    claimTag(status) {
      return claimTag(status);
    },
    statusIcon(status) {
      return STATUS_ICON[status] || "📋";
    },
    statusBg(status) {
      return STATUS_BG[status] || "var(--v-line-2)";
    },
    statusLabel(status) {
      return this.$t("claims.status." + status);
    },
    sourceLabel(source) {
      if (!source) return "—";
      const key = "claims.source." + source;
      const label = this.$t(key);
      return label === key ? source : label;
    },
    fInt(n) {
      return new Intl.NumberFormat("ar-EG", { numberingSystem: "latn" }).format(n || 0);
    },
    fMoney(n) {
      return this.fInt(n) + " " + this.$t("claims.currency");
    },
    fDate(iso) {
      if (!iso) return "—";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
  },
};
</script>

<style scoped>
.adj {
  border: 1px solid var(--v-line-2);
  border-radius: 10px;
  background: #fff;
  padding: 10px 12px;
}
.adj__title {
  font-weight: 700;
  margin-bottom: 6px;
}
.adj__rules {
  margin-bottom: 0;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field input,
.field select,
.field textarea {
  padding: 8px 10px;
  border: 1px solid var(--v-line-2);
  border-radius: 8px;
  font: inherit;
  background: #fff;
}
.err {
  color: var(--v-danger);
}
</style>
